import React, { useEffect, useState, useRef } from 'react';
import WeatherWidget from '../../components/WeatherWidget';
import VehicleTrackerMap from '../../components/VehicleTrackerMap';
import SolarMonitoring from '../../components/SolarMonitoring/SolarMonitoring';
import { FaClipboardList, FaCalculator, FaBriefcase } from 'react-icons/fa';
import { 
  getEnquiriesCreatedToday, 
  getEnquiriesCreatedThisMonth,
  getQuotesCreatedToday,
  getQuotesCreatedThisMonth,
  getJobsCreatedToday,
  getJobsCreatedThisMonth
} from '../../data/dashboardData';
import GetJsonData from '../../data/dataAccess';
import './Dashboard.css';

const Dashboard = () => {
  const [enquiryStats, setEnquiryStats] = useState({ today: 0, month: 0 });
  const [quoteStats, setQuoteStats] = useState({ today: 0, month: 0 });
  const [jobStats, setJobStats] = useState({ today: 0, month: 0 });
  const [latestNews, setLatestNews] = useState<any[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  
  // Video refs for CCTV feeds
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);
  const video3Ref = useRef<HTMLVideoElement>(null);
  const video4Ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [enquiryToday, enquiryMonth, quoteToday, quoteMonth, jobToday, jobMonth] = await Promise.all([
          getEnquiriesCreatedToday(),
          getEnquiriesCreatedThisMonth(),
          getQuotesCreatedToday(),
          getQuotesCreatedThisMonth(),
          getJobsCreatedToday(),
          getJobsCreatedThisMonth()
        ]);

        setEnquiryStats({ today: enquiryToday, month: enquiryMonth });
        setQuoteStats({ today: quoteToday, month: quoteMonth });
        setJobStats({ today: jobToday, month: jobMonth });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      }
    };

    const fetchLatestNews = async () => {
      try {
        setNewsLoading(true);
        // Fetch news from backend API
        const data = await GetJsonData('Dashboard/News');
        
        console.log('News API Response:', data);
        console.log('News data type:', typeof data);
        console.log('News data length:', data?.length);
        
        if (data && Array.isArray(data) && data.length > 0) {
          setLatestNews(data);
        } else if (data && typeof data === 'object') {
          // Check if data is wrapped in a property
          console.log('News data keys:', Object.keys(data));
          const newsArray = data.articles || data.items || data.news || data;
          if (Array.isArray(newsArray) && newsArray.length > 0) {
            setLatestNews(newsArray);
          } else {
            setLatestNews([
              {
                title: 'No News Available',
                description: 'Unable to load news feed. Please check back later.',
                link: 'https://www.bbc.com/news'
              }
            ]);
          }
        } else {
          setLatestNews([
            {
              title: 'No News Available',
              description: 'Unable to load news feed. Please check back later.',
              link: 'https://www.bbc.com/news'
            }
          ]);
        }
      } catch (error: any) {
        console.error('Error fetching latest news:', error);
        console.error('Error response:', error.response);
        console.error('Error status:', error.response?.status);
        console.error('Error data:', error.response?.data);
        console.error('Error message:', error.message);
        // Set default news on error
        setLatestNews([
          {
            title: 'Error Loading News',
            description: `Unable to load news feed. Error: ${error.response?.status || error.message}`,
            link: 'https://www.bbc.com/news'
          }
        ]);
      } finally {
        setNewsLoading(false);
      }
    };

    fetchStats();
    fetchLatestNews();

    // Set up 5-minute auto-refresh interval
    const refreshInterval = setInterval(() => {
      fetchStats();
      fetchLatestNews();
    }, 5 * 60 * 1000); // 5 minutes in milliseconds

    // Cleanup interval on component unmount
    return () => clearInterval(refreshInterval);
  }, []);

  // Ensure all CCTV videos play
  useEffect(() => {
    const playVideos = async () => {
      const videos = [video1Ref, video2Ref, video3Ref, video4Ref];
      for (const videoRef of videos) {
        if (videoRef.current) {
          try {
            await videoRef.current.play();
          } catch (error) {
            console.error('Error playing video:', error);
          }
        }
      }
    };
    
    // Small delay to ensure videos are loaded
    const timer = setTimeout(playVideos, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="dashboard-container">
      {/* Header Banner with Strapline */}
      <div className="dashboard-header">
        <div className="header-left">
          <div className="bamfords-logo">
            <img src="/static/media/BamfordsLogoWhite.08884d1541f80614498bc484f2fddbf5.svg" alt="Bamfords Logo" />
          </div>
        </div>
        <div className="header-center">
          <div className="header-strapline">
            <span className="strapline-bullet">●</span> Identify <span className="strapline-bullet">●</span> Resolve <span className="strapline-bullet">●</span> Prevent <span className="strapline-bullet">●</span>
          </div>
        </div>
        <div className="header-right">
          <WeatherWidget />
        </div>
      </div>

      {/* Main Content - Three Column Layout */}
      <div className="dashboard-grid-new">
        {/* Left Column - Vehicle Trackers Full Height */}
        <div className="dashboard-section vehicle-section">
          <div className="section-header">
            <h2>Vehicle Trackers</h2>
          </div>
          <div className="quarter-content map-content">
            <VehicleTrackerMap 
              center={{ lat: 50.9416, lng: -2.6321 }}
              zoom={10}
            />
          </div>
        </div>

        {/* Middle Column - CCTV and Solar Stacked */}
        <div className="dashboard-middle-column">
          {/* CCTV Section */}
          <div className="dashboard-section cctv-section">
            <div className="section-header">
              <h2>CCTV</h2>
            </div>
            <div className="cctv-container">
              <div className="cctv-grid">
                <div className="cctv-feed">
                  <video
                    ref={video1Ref}
                    src="http://192.168.8.193:1984/api/stream.mp4?src=Camera5"
                    width="100%"
                    height="100%"
                    style={{ background: '#000' }}
                    autoPlay
                    playsInline
                    controls
                    muted
                  />
                  <div className="feed-label">Camera 5</div>
                </div>
                <div className="cctv-feed">
                  <video
                    ref={video2Ref}
                    src="http://192.168.8.193:1984/api/stream.mp4?src=Camera7"
                    width="100%"
                    height="100%"
                    style={{ background: '#000' }}
                    autoPlay
                    playsInline
                    controls
                    muted
                  />
                  <div className="feed-label">Camera 7</div>
                </div>
                <div className="cctv-feed">
                  <video
                    ref={video3Ref}
                    src="http://192.168.8.193:1984/api/stream.mp4?src=Camera4"
                    width="100%"
                    height="100%"
                    style={{ background: '#000' }}
                    autoPlay
                    playsInline
                    controls
                    muted
                  />
                  <div className="feed-label">Camera 4</div>
                </div>
                <div className="cctv-feed">
                  <video
                    ref={video4Ref}
                    src="http://192.168.8.193:1984/api/stream.mp4?src=Camera9"
                    width="100%"
                    height="100%"
                    style={{ background: '#000' }}
                    autoPlay
                    playsInline
                    controls
                    muted
                  />
                  <div className="feed-label">Camera 9</div>
                </div>
              </div>
            </div>
          </div>

          {/* Solar Section */}
          <div className="dashboard-section solar-section">
            <SolarMonitoring />
          </div>
        </div>

        {/* Right Column - Narrow Stats and News */}
        <div className="dashboard-right-column">
          {/* Stats Cards - Stacked Vertically */}
          <div className="dashboard-section stats-column-section">
            {/* Enquiries Card */}
            <div className="stat-card-column enquiry-card">
              <div className="stat-card-header">
                <span className="stat-icon"><FaClipboardList /></span>
                <span className="stat-title">Enquiries</span>
              </div>
              <div className="stat-card-content">
                <div className="stat-row">
                  <span className="stat-period">Today</span>
                  <span className="stat-number primary">{enquiryStats.today}</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-row">
                  <span className="stat-period">This Month</span>
                  <span className="stat-number">{enquiryStats.month}</span>
                </div>
              </div>
            </div>

            {/* Quotes Card */}
            <div className="stat-card-column quote-card">
              <div className="stat-card-header">
                <span className="stat-icon"><FaCalculator /></span>
                <span className="stat-title">Quotes</span>
              </div>
              <div className="stat-card-content">
                <div className="stat-row">
                  <span className="stat-period">Today</span>
                  <span className="stat-number primary">{quoteStats.today}</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-row">
                  <span className="stat-period">This Month</span>
                  <span className="stat-number">{quoteStats.month}</span>
                </div>
              </div>
            </div>

            {/* Jobs Card */}
            <div className="stat-card-column job-card">
              <div className="stat-card-header">
                <span className="stat-icon"><FaBriefcase /></span>
                <span className="stat-title">Jobs</span>
              </div>
              <div className="stat-card-content">
                <div className="stat-row">
                  <span className="stat-period">Today</span>
                  <span className="stat-number primary">{jobStats.today}</span>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-row">
                  <span className="stat-period">This Month</span>
                  <span className="stat-number">{jobStats.month}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Latest News */}
          <div className="dashboard-section news-column-section">
            <div className="section-header">
              <h2>Latest News</h2>
            </div>
            <div className="news-container-column">
              {newsLoading ? (
                <div className="placeholder-text">
                  <p>Loading latest news...</p>
                </div>
              ) : latestNews.length > 0 ? (
                <div className="news-list">
                  {latestNews.map((article, index) => (
                    <div key={index} className="news-item">
                      <h4>{article.title}</h4>
                      {article.link && (
                        <a href={article.link} target="_blank" rel="noopener noreferrer">
                          Read more
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="placeholder-text">
                  <p>BBC/Sky News Feed</p>
                  <p style={{ fontSize: '0.9em', color: '#999' }}>No news available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
